/**
 * LPP-25233: regole degli accessori con gestione speciale aggiunti post-firma, allineate all'emissione.
 *
 * NTW rivalida tutto lato server (PostSaleAncillaryValidator: ERROR_POST_SIGN_*, ERROR_TOLL_ANCILLARY);
 * qui si evita all'operatore di firmare un blocco che verrebbe rifiutato.
 */
import {AncillarySubtype} from "../../create-ra/ra-filling/config";
import {ERROR_DETAIL_LIST} from "../../create-ra/config";
import {IRADAncillary} from "../../models/models";

/** Guidatori aggiuntivi massimi su un RA: slot `firstDriver` + `driver`, come in emissione. */
export const MAX_ADDITIONAL_DRIVERS = 2;

/** Tipologie che richiedono l'indirizzo di consegna/ritiro (regola yup di emissione, ra-filling/config.ts). */
export const ADDRESS_SUBTYPES: string[] = [
	AncillarySubtype.Delivery, AncillarySubtype.Collection, AncillarySubtype.UrbanDelivery, AncillarySubtype.UrbanCollection
];

export const isMileage = (a: IRADAncillary) => Boolean((a as any)?.mileageNeeded);
export const needsAddress = (a: IRADAncillary) => ADDRESS_SUBTYPES.includes((a?.ancillarySubType as string) ?? '');
export const isAdditionalDriverAncillary = (a: IRADAncillary) => a?.ancillarySubType === AncillarySubtype.AdditionalDrivers;

/** Telepedaggio: in emissione il codice "ET" (RaFilling), in NTW il subtype che contiene "toll". */
export const isEtollAncillary = (a: IRADAncillary) =>
	a?.ancillaryCode === 'ET' || String(a?.ancillarySubType ?? '').toUpperCase().includes('TOLL');

/** Come in emissione: il telepedaggio non e' vendibile se il veicolo del RA non ha l'apparato. */
export const isEtollBlocked = (a: IRADAncillary, etollEquipmentNumber?: string | null) =>
	isEtollAncillary(a) && String(etollEquipmentNumber ?? '').trim().length === 0;

/** Guidatori aggiuntivi gia' sul RA, contati come li mostra il dettaglio (RaDetail: nome e cognome valorizzati). */
export const countAdditionalDrivers = (driversInfo: any): number =>
	[driversInfo?.firstDriver, driversInfo?.driver].filter((d: any) => !!d?.firstName && !!d?.lastName).length;

/** Unita' di ADDITIONAL_DRIVER vendibili: il listino, ma mai oltre gli slot liberi del RA. */
export const additionalDriverUnitsCap = (maxUnits: number, existingDrivers: number) =>
	Math.max(0, Math.min(maxUnits, MAX_ADDITIONAL_DRIVERS - existingDrivers));

/** Regola yup di emissione: indirizzo obbligatorio se l'accessorio e' selezionato (unita' o km). */
export const isAddressMissing = (a: IRADAncillary, units: number, mileage: number, address: string) =>
	needsAddress(a) && (units > 0 || mileage > 0) && address.trim().length === 0;

export interface IAddItemState {
	ancillary: IRADAncillary;
	units: number;
	mileage: number;
	address: string;
	/** cbId dei guidatori aggiuntivi pronti (null = dati/controlli/OTP non completati), uno per unita'. */
	driverCbIds: (string | null)[];
}

/** Un accessorio selezionato e' inviabile solo con indirizzo, km e guidatori completi. */
export const isAddItemComplete = ({ancillary, units, mileage, address, driverCbIds}: IAddItemState) =>
	units <= 0 || (
		!isAddressMissing(ancillary, units, mileage, address)
		&& (!isMileage(ancillary) || mileage > 0)
		&& (!isAdditionalDriverAncillary(ancillary) || (driverCbIds.length === units && driverCbIds.every(Boolean)))
	);

export interface IPostSignDriverStatus {
	/** cbId restituito da POST /network/ancillary/post-sign/driver. */
	registeredCbId: string | null;
	/** cbId attualmente nel form: se l'operatore svuota o sceglie un altro cliente, la registrazione non vale piu'. */
	formCbId: string | null | undefined;
	/** Esito di POST .../driver/check (blacklist + patente MCTC). */
	checkPassed: boolean | null;
	otpDone: boolean;
	verifiedDocuments: boolean | null | undefined;
}

/**
 * Guidatore pronto per la firma. I documenti seguono il gate di emissione (CustomerData, "avanti"
 * disabilitato con cbId e verifiedDocuments valorizzato ma non true).
 */
export const isPostSignDriverReady = (s: IPostSignDriverStatus) =>
	!!s.registeredCbId
	&& String(s.formCbId ?? '') === s.registeredCbId
	&& s.checkPassed === true
	&& s.otpDone
	&& (s.verifiedDocuments === null || s.verifiedDocuments === true);

/** Codici "ReturnErrors" (lista `[{code}]`) di sc-be per il guidatore aggiuntivo 1, con i messaggi di CustomerData. */
const DRIVER_RETURN_ERRORS: Record<string, string> = {
	additional_driver_1_fiscal_code: 'customerData.message.error.fiscalCode',
	additional_driver_1_duplicate: 'customerData.message.error.additionalDriver1Duplicate',
	additional_driver_1_identity_card_duplicate: 'customerData.message.error.additionalDriver1IdentityCardDuplicate',
	identity_card_duplicate: 'customerData.message.error.additionalDriver1IdentityCardDuplicate'
};

/**
 * Messaggio per un errore di POST /network/ancillary/post-sign/driver. sc-be risponde con gli stessi codici
 * dell'emissione per il guidatore aggiuntivo 1: lista `[{code}]`, `ErrorResponse.errorDetails` (codice tra
 * virgolette, mappato da ERROR_DETAIL_LIST) o il codice come body.
 */
export const postSignDriverErrorMessage = (err: unknown): {key: string, params?: Record<string, string>} => {
	const data = (err as any)?.response?.data;
	if (Array.isArray(data)) {
		const known = data.map((e: any) => DRIVER_RETURN_ERRORS[e?.code]).find(Boolean);
		return {key: known ?? 'customerData.message.error.tryLater'};
	}
	const code = String(data?.errorDetails ?? data ?? '').replace(/"/g, '').trim();
	const entry: any = Object.entries(ERROR_DETAIL_LIST).find(([k]) => k.replace(/"/g, '') === code)?.[1];
	if (entry) {
		return entry.addingMessage
			? {key: `customerData.message.error.${entry.message}`, params: {detail: entry.addingMessage(data?.errorMessage ?? '')}}
			: {key: `customerData.message.error.${entry.message}`};
	}
	if (code === 'ERROR_POST_SIGN_RA_NOT_ONGOING') {
		return {key: 'raDetail.ancillaries.postSign.message.reason.ERROR_POST_SIGN_RA_NOT_ONGOING'};
	}
	return {key: 'customerData.message.error.tryLater'};
};
